import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ENVIRONMENT, TranslateLoader } from '@cfp/core';
import { Button, Icon, Link } from '@cfp/ui/common';
import {
  FormGroupAfterLabelDirective,
  FormGroupComponent,
  InputRadio,
  InputText,
  Select,
  SelectOption,
  ValidationFeedback
} from '@cfp/ui/forms';
import { CfpCurrencyPipe, CurrencySymbolPipe } from '@cfp/ui/pipes';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CurrencyCodeDTO } from 'csr-asset-app';
import { CountryCodeDTO } from 'csr-login-app';
import { map, tap } from 'rxjs';
import { SelfOnboardingCreateAccountData } from '../self-onboarding-create-account-data';
import { SelfOnboardingCreateAccountSector } from '../self-onboarding-create-account-sector';

@Component({
  selector: 'cfp-self-onboarding-create-account',
  templateUrl: './self-onboarding-create-account.html',
  styleUrls: ['./self-onboarding-create-account.scss'],
  imports: [
    InputRadio,
    InputText,
    Select,
    SelectOption,
    ValidationFeedback,
    Button,
    Link,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    FormGroupComponent,
    FormGroupAfterLabelDirective,
    Icon,
    CurrencySymbolPipe
  ],
  providers: [CfpCurrencyPipe, CurrencyPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelfOnboardingCreateAccount {
  public readonly isLoading = input(false, { alias: 'loading' });
  public readonly showAnnualRevenue = input(false);
  public readonly showCountryOfIncorporation = input(false);
  public readonly showSector = input(false);
  public readonly showRelationManagerName = input(false);
  public readonly currencyCode = input<CurrencyCodeDTO>(CurrencyCodeDTO.GBP);
  public readonly previous = output<void>();
  public readonly next = output<SelfOnboardingCreateAccountData>();

  protected readonly isExistingCustomer = signal<boolean | undefined>(undefined);
  protected formGroup: FormGroup;
  protected countries: CountryCodeDTO[] = [
    CountryCodeDTO.IN,
    CountryCodeDTO.SG,
    CountryCodeDTO.HK,
    CountryCodeDTO.FR,
    CountryCodeDTO.MX
  ];
  protected sectors: { value: string; label: string }[] = [];

  private readonly environment = inject(ENVIRONMENT);
  private readonly formBuilder = inject(FormBuilder);
  private readonly elementRef = inject(ElementRef);
  private readonly translateService = inject(TranslateService);
  private readonly translateLoader = inject(TranslateLoader);

  protected readonly isTranslationLoaded = toSignal(
    this.translateLoader.loadTranslation('self-onboarding').pipe(
      tap(() => {
        if (!this.formGroup) {
          this.initializeSectors();
          this.buildForm();
        }
      }),
      map(() => true)
    )
  );

  protected readonly privacyLink = this.environment.components.privacyLink;
  protected readonly nonExistingCustomerExternalUrl = this.environment.selfOnboarding?.nonExistingCustomerExternalUrl;
  protected readonly relationManagerNameRequired = this.environment.selfOnboarding?.relationManagerNameRequired;

  constructor() {
    effect(() => {
      if (!this.formGroup) {
        return;
      }
      if (this.isLoading()) {
        this.formGroup.disable();
      } else {
        this.formGroup.enable();
      }
    });
  }

  private focusInvalidControl(): void {
    const invalidFormGroups: HTMLInputElement[] = this.elementRef.nativeElement.querySelectorAll(
      '.has-validation-feedback-danger'
    );
    if (invalidFormGroups.length > 0) {
      const control: HTMLElement | null = invalidFormGroups[0].querySelector('input,select');
      if (control) {
        control.focus();
      }
    }
  }

  protected continue(): void {
    if (this.formGroup.invalid) {
      setTimeout(() => {
        this.focusInvalidControl();
      });
      return;
    }

    this.next.emit({
      companyName: this.formGroup.value.companyName,
      companyEmail: this.formGroup.value.companyEmail,
      contactName: this.formGroup.value.contactName,
      annualRevenue: this.formGroup.value.annualRevenue,
      countryOfIncorporation: this.formGroup.value.countryOfIncorporation,
      sector: this.formGroup.value.sector,
      relationManagerName: this.formGroup.value.relationManagerName
    });
  }

  private initializeSectors(): void {
    const sectorMap: { [key in SelfOnboardingCreateAccountSector]: string } = {
      [SelfOnboardingCreateAccountSector.HOSPITALITY_AND_FOOD_SERVICES]: 'Hospitality and Food services',
      [SelfOnboardingCreateAccountSector.REAL_ESTATE_ACTIVITIES]: 'Real Estate activities',
      [SelfOnboardingCreateAccountSector.CONSTRUCTION]: 'Construction',
      [SelfOnboardingCreateAccountSector.MEDICAL_AND_HEALTH_ACTIVITIES]: 'Medical and Health activities',
      [SelfOnboardingCreateAccountSector.ECOMMERCE_AND_CONSUMER_GOODS]: 'E-commerce and Consumer Goods',
      [SelfOnboardingCreateAccountSector.WHOLESALE_AND_RETAIL_TRADE]: 'Wholesale and Retail Trade',
      [SelfOnboardingCreateAccountSector.FINANCIAL_AND_INSURANCE_ACTIVITIES]: 'Financial and Insurance activities',
      [SelfOnboardingCreateAccountSector.AGRICULTURE_FORESTRY_AND_FISHING]: 'Agriculture, Forestry and Fishing',
      [SelfOnboardingCreateAccountSector.TRANSPORTATION_LOGISTIC_AND_STORAGE]: 'Transportation, Logistic and Storage',
      [SelfOnboardingCreateAccountSector.MANUFACTURING_AND_INDUSTRIAL_SERVICES]:
        'Manufacturing and Industrial services',
      [SelfOnboardingCreateAccountSector.ENERGY_POWER_AND_RENEWABLES]: 'Energy, Power and Renewables',
      [SelfOnboardingCreateAccountSector.EDUCATION]: 'Education',
      [SelfOnboardingCreateAccountSector.ARTS_ENTERTAINMENT_AND_RECREATION]: 'Arts, Entertainment and Recreation',
      [SelfOnboardingCreateAccountSector.PROFESSIONAL_AND_CONSULTANCY_ACTIVITIES]:
        'Professional and Consultancy activities',
      [SelfOnboardingCreateAccountSector.INFORMATION_AND_COMMUNICATION]: 'Information and Communication',
      [SelfOnboardingCreateAccountSector.OTHER_SERVICES]: 'Other services'
    };

    const sectorValues = Object.values(SelfOnboardingCreateAccountSector) as string[];
    this.sectors = sectorValues
      .map((sector) => ({
        value: sectorMap[sector],
        label: this.translateService.instant(`selfOnboarding.createAccount.sector.${sector}`)
      }))
      .sort((a: { value: string; label: string }, b: { value: string; label: string }) => {
        if (a.value === sectorMap[SelfOnboardingCreateAccountSector.OTHER_SERVICES]) return 1;
        if (b.value === sectorMap[SelfOnboardingCreateAccountSector.OTHER_SERVICES]) return -1;
        return a.label.localeCompare(b.label);
      });
  }

  private buildForm(): void {
    const countryOfIncorporationValidators = this.showCountryOfIncorporation() ? [Validators.required] : [];
    const relationManagerNameValidators =
      this.showRelationManagerName() && this.relationManagerNameRequired ? [Validators.required] : [];
    this.formGroup = this.formBuilder.group({
      companyName: [null, [Validators.required]],
      companyEmail: [null, [Validators.required, Validators.email]],
      contactName: [null, [Validators.required]],
      annualRevenue: [null, []],
      countryOfIncorporation: [null, countryOfIncorporationValidators],
      sector: [null, []],
      relationManagerName: [null, relationManagerNameValidators]
    });
  }
}
